import { useState, useEffect, type Dispatch, type SetStateAction } from 'react'

// A custom hook that works like useState but reverts to the initialState
// after a specified timeout. Practical if you want to display a notification
// for a specific amount of time.

// Example:
// The notification will be displayed for 2.5 seconds
//
// const [userNotification, setUserNotification] = useTimeoutState(null, 2500)
//
// return (
//     <>
//         { userNotification && <Notification>{userNotification}</Notification>}
//         <button onClick={() => setUserNotification('Button clicked')}
//     </>
// )

export function useTimeoutState<T>(initialState: T, timeout: number): [T, Dispatch<SetStateAction<T>>] {
    const [state, setState] = useState<T>(initialState)

    useEffect(() => {
        const timeoutCb = state !== initialState ? setTimeout(() => setState(initialState), timeout) : null

        return () => {
            if (timeoutCb) {
                clearTimeout(timeoutCb)
            }
        }
    }, [state, initialState, timeout])

    return [state, setState]
}
